import React, { useEffect, useState } from "react";
import Icons from "../icons";
import HelpButton from "./HelpDialog";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25";

/* ------------------------------------------------------------------ */
/*  Shared presentational helpers                                      */
/* ------------------------------------------------------------------ */

function Row({ label, value, mono = true }) {
  return (
    <div className="flex flex-col gap-1 border-b border-slate-100 py-2.5 last:border-b-0 sm:flex-row sm:items-center sm:gap-4">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500 sm:w-56">
        {label}
      </div>
      <div
        className={[
          "min-w-0 flex-1 break-all text-sm text-slate-900",
          mono ? "font-mono" : "",
        ].join(" ")}
      >
        {value ? value : <span className="italic text-slate-400">not set</span>}
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, onEdit, children }) {
  return (
    <section className="rounded-2xl bg-card p-5 shadow-sm ring-1 ring-slate-200">
      <div className="mb-3 flex items-center gap-2">
        {Icon && (
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
            <Icon className="h-4 w-4" />
          </span>
        )}
        <h3 className="flex-1 text-sm font-semibold text-slate-900">{title}</h3>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            title={`Edit ${title}`}
            className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-brand-600"
          >
            <Icons.Pencil className="h-4 w-4" />
          </button>
        )}
      </div>
      <div>{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Edit dialog                                                        */
/* ------------------------------------------------------------------ */

/**
 * Generic modal that edits a flat { key: value } object.
 *
 * Props:
 *   title      – dialog header
 *   fields     – [{ key, label, mono? }]  field definitions (render order)
 *   values     – the original values (read-only reference for Reset)
 *   onCancel   – close without saving
 *   onSave     – (editedValues) => Promise<void>
 */
function EditDialog({ title, fields, values, onCancel, onSave }) {
  const [draft, setDraft] = useState({ ...values });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (key, val) => setDraft((d) => ({ ...d, [key]: val }));

  const handleReset = () => {
    setDraft({ ...values });
    setError("");
  };

  const handleCancel = () => {
    setDraft({ ...values });
    onCancel();
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await onSave(draft);
    } catch (e) {
      setError(e.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saving && handleCancel();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className={`flex ${DIALOG_HEIGHT} w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200`}>
        {/* Header */}
        <div className="flex flex-none items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">
            Edit {title}
          </h2>
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {fields.map((f) => (
            <label key={f.key} className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">
                {f.label}
              </span>
              <input
                value={draft[f.key] ?? ""}
                onChange={(e) => set(f.key, e.target.value)}
                className={inputCls}
              />
            </label>
          ))}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer: Reset · Cancel · Save */}
        <div className="flex flex-none items-center justify-between border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={handleReset}
            disabled={saving}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Reset
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-60"
            >
              {saving && <Icons.Spinner className="h-4 w-4 animate-spin" />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Section field definitions (key must match the backend JSON shape)   */
/* ------------------------------------------------------------------ */

const SECTION_FIELDS = {
  app: [
    { key: "appName", label: "App Name" },
    { key: "appEnv", label: "Environment" },
  ],
  testTarget: [
    { key: "startUrl", label: "App Start URL" },
    { key: "apiBase", label: "PlantOps API Base" },
  ],
  ai: [
    { key: "provider", label: "Provider" },
    { key: "model", label: "Model" },
    { key: "openAiBaseUrl", label: "OpenAI Base URL" },
  ],
  browser: [
    { key: "headless", label: "Headless" },
    { key: "slowMo", label: "Slow-Mo (ms)" },
  ],
  github: [
    { key: "repoUi", label: "UI Repo" },
    { key: "repoApi", label: "API Repo" },
    { key: "defaultBranch", label: "Default Branch" },
  ],
  jira: [
    { key: "baseUrl", label: "Base URL" },
    { key: "email", label: "Email" },
  ],
};

const SECTION_TITLES = {
  app: "Application",
  testTarget: "Test Target",
  ai: "AI Provider",
  browser: "Browser",
  github: "GitHub",
  jira: "Jira",
};

/* ------------------------------------------------------------------ */
/*  Main page component                                                */
/* ------------------------------------------------------------------ */

export default function AppConfigView() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Which section is open for editing: "app" | "testTarget" | … | null.
  const [editing, setEditing] = useState(null);

  const loadConfig = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}appconfig`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.detail || "Failed to load application configuration.");
      } else {
        setConfig(body?.data || null);
      }
    } catch {
      setError("Network error while loading configuration.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const saveSection = async (sectionKey, values) => {
    const res = await fetch(`${API_BASE_URL}appconfig`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [sectionKey]: values }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(body?.detail || `HTTP ${res.status}`);
    }
    // Apply the full merged config returned by the server.
    setConfig(body?.data || null);
    setEditing(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Application Configuration
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Runtime settings the server is using — test target, AI provider, and
            integrations.
          </p>
        </div>
        <HelpButton screen="appconfig" />
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Icons.Spinner className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && config && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card
            title="Application"
            icon={Icons.Home}
            onEdit={() => setEditing("app")}
          >
            <Row label="App Name" value={config.app?.appName} mono={false} />
            <Row label="Environment" value={config.app?.appEnv} />
          </Card>

          <Card
            title="Test Target"
            icon={Icons.Play}
            onEdit={() => setEditing("testTarget")}
          >
            <Row label="App Start URL" value={config.testTarget?.startUrl} />
            <Row label="PlantOps API Base" value={config.testTarget?.apiBase} />
          </Card>

          <Card
            title="AI Provider"
            icon={Icons.Spark}
            onEdit={() => setEditing("ai")}
          >
            <Row label="Provider" value={config.ai?.provider} />
            <Row label="Model" value={config.ai?.model} />
            <Row label="OpenAI Base URL" value={config.ai?.openAiBaseUrl} />
          </Card>

          <Card
            title="Browser"
            icon={Icons.Eye}
            onEdit={() => setEditing("browser")}
          >
            <Row label="Headless" value={config.browser?.headless} />
            <Row label="Slow-Mo (ms)" value={config.browser?.slowMo} />
          </Card>

          <Card
            title="GitHub"
            icon={Icons.Boards}
            onEdit={() => setEditing("github")}
          >
            <Row label="UI Repo" value={config.github?.repoUi} />
            <Row label="API Repo" value={config.github?.repoApi} />
            <Row label="Default Branch" value={config.github?.defaultBranch} />
          </Card>

          <Card
            title="Jira"
            icon={Icons.List}
            onEdit={() => setEditing("jira")}
          >
            <Row label="Base URL" value={config.jira?.baseUrl} />
            <Row label="Email" value={config.jira?.email} />
          </Card>
        </div>
      )}

      {/* Edit dialog — opens for whichever section was clicked */}
      {editing && config?.[editing] && (
        <EditDialog
          title={SECTION_TITLES[editing] || editing}
          fields={SECTION_FIELDS[editing]}
          values={config[editing]}
          onCancel={() => setEditing(null)}
          onSave={(values) => saveSection(editing, values)}
        />
      )}
    </div>
  );
}
